import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTrash,
  FaEdit,
  FaEye,
  FaPhoneAlt,
  FaFilter,
} from "react-icons/fa";
import AllUserListModal from "../../components/Modals/user/AllUserListModal";
import DeleteModal from "../../components/Modals/user/DeleteModal";
import {
  InActiveUserList as fetchInActiveUsers,
  userDetails,
  deleteUser,
  updateUserStatus,
} from "../../api/endpoints";
import Swal from "../../utils/swalCompat";
import PageHeader from "../../components/PageHeader";

const InActiveUserList = () => {
  const navigate = useNavigate();

  // Modal + Data States
  const [showAllUserListModal, setShowAllUserListModal] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [inActiveUsers, setInActiveUsers] = useState([]);
  const [editData, setEditData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Filters + Pagination
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [showFilter, setShowFilter] = useState(false);

  // Loading States
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [fetchingUserId, setFetchingUserId] = useState(null); // ✅ Edit button loader

  // ============================================================
  // FETCH INACTIVE USERS
  // ============================================================
  const inactiveuserList = async () => {
    try {
      setLoading(true);
      const params = { page, limit };

      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;

      console.log("PARAMS", params);
      const res = await fetchInActiveUsers(params);

      setInActiveUsers(res.data.users || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    inactiveuserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, page]);

  // ============================================================
  // ✅ HANDLE VIEW — Navigate to page
  // ============================================================
  const handleViewUserDetails = (userId) => {
    console.log("➡️ Navigating to /user-details/" + userId);
    navigate(`/user-details/${userId}`);
  };

  // ============================================================
  // ✅ HANDLE EDIT — Opens Modal with GET /admin/user-details/:id
  // ============================================================
  const handleEdit = async (userId) => {
    try {
      setFetchingUserId(userId);
      setShowAllUserListModal(false);
      setEditData(null);

      const res = await userDetails(userId);
      console.log("✅ EDIT USER DATA:", res);
      console.log("✅ URL hit: /admin/user-details/" + userId);

      const userData =
        res?.data?.data ||
        res?.data?.user ||
        res?.user ||
        res?.data ||
        res;

      console.log("✅ Extracted user (EDIT):", userData);

      setEditData(userData);
      setViewMode(false);

      setTimeout(() => {
        setShowAllUserListModal(true);
      }, 50);
    } catch (error) {
      console.error("❌ Edit error:", error);
      Swal.fire("Error", "Failed to fetch user details", "error");
    } finally {
      setFetchingUserId(null);
    }
  };

  // ============================================================
  // DELETE
  // ============================================================
  const handleDelete = async () => {
    try {
      await deleteUser({ userId: deleteId });
      setShowDeleteModal(false);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "User has been deleted successfully.",
        timer: 2000,
        showConfirmButton: false,
        position: "top-end",
        toast: true,
      });

      inactiveuserList();
    } catch (error) {
      console.log("DELETE ERROR:", error);

      Swal.fire({
        icon: "error",
        title: "Delete Failed!",
        text: error.response?.data?.message || "Failed to delete user.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  // ============================================================
  // STATUS TOGGLE
  // ============================================================
  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";

    try {
      setTogglingId(userId);
      setLoading(true);

      const response = await updateUserStatus({
        userId: userId,
        status: newStatus,
      });

      console.log("Status Update Response:", response);
      inactiveuserList();
    } catch (error) {
      console.log("STATUS UPDATE ERROR:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update user status",
        "error"
      );
    } finally {
      setLoading(false);
      setTogglingId(null);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lead-page">
      <PageHeader
        title="Inactive User List"
      />
      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2 align-items-center">
            {showFilter && (
              <>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search User"
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                />
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    setPage(1);
                    setStatus(e.target.value);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </>
            )}
            <button
              className="btn btn-dark"
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              Filter
            </button>
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Referral</th>
                  {/* <th>KYC</th> */}
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : inActiveUsers.length > 0 ? (
                  inActiveUsers.map((item, index) => (
                    <tr key={item._id}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          {item.profileImage ? (
                            <img
                              src={item.profileImage}
                              width="35"
                              height="35"
                              className="rounded-circle"
                              alt=""
                            />
                          ) : (
                            <div
                              className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                              style={{
                                width: "35px",
                                height: "35px",
                              }}
                            >
                              {item.fullName?.charAt(0) || "U"}
                            </div>
                          )}
                          <span>{item.fullName || "N/A"}</span>
                        </div>
                      </td>
                      <td>
                        <FaPhoneAlt size={12} /> {item.mobile}
                      </td>
                      <td>
                        <span className="badge bg-info">
                          {item.referralCode}
                        </span>
                      </td>
                      {/* <td>
                        <span
                          className={`badge ${
                            item.isKycVerified ? "bg-success" : "bg-warning"
                          }`}
                        >
                          {item.isKycVerified ? "Verified" : "Pending"}
                        </span>
                      </td> */}
                      <td>
                        <div className="toggle-switch-container">
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={item.status === "ACTIVE"}
                              onChange={() =>
                                handleStatusToggle(item._id, item.status)
                              }
                              disabled={togglingId === item._id || loading}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                          <span
                            className={`status-text ${
                              item.status === "ACTIVE"
                                ? "active-text"
                                : "inactive-text"
                            }`}
                          ></span>
                        </div>
                      </td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="d-flex gap-2">
                          {/* ✅ VIEW BUTTON — Navigate to page */}
                          <button
                            className="btn btn-sm btn-info custum_primary"
                            onClick={() => handleViewUserDetails(item._id)}
                            title="View User Details"
                          >
                            <FaEye />
                          </button>

                        

                          {/* ✅ DELETE BUTTON */}
                          <button
                            className="btn btn-sm btn-info custum_danger"
                            onClick={() => {
                              setDeleteId(item._id);
                              setShowDeleteModal(true);
                            }}
                            title="Delete User"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No Inactive Users Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-center gap-2 mt-3">
            <button
              className="btn btn-sm btn-secondary"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span className="mt-1">
              Page {page} of {totalPages}
            </span>
            <button
              className="btn btn-sm btn-secondary"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* ✅ EDIT MODAL */}
      <AllUserListModal
        show={showAllUserListModal}
        setShow={setShowAllUserListModal}
        editData={editData}
        viewMode={viewMode}
      />

      <DeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleDelete}
        title="Delete User?"
        message="Are you sure you want to delete this user?"
      />
    </div>
  );
};

export default InActiveUserList;